// Credit accounting. Credits are an offchain balance kept by the coordinator in
// an append-only ledger of integer micro-credits (1 credit = 1_000_000 µcr).
// They are not tokens, they are not transferable, and they never touch a chain.
//
// Expected-work accounting: a share at difficulty `bits` is a hash with at
// least `bits` leading zero bits. Each hash succeeds with probability 2^-bits,
// so one accepted share is evidence of 2^bits hashes of expected work, no
// matter which difficulty the miner picked. Credits are paid per unit of
// expected work:
//
//   credit(share) = 2^bits / WORK_PER_CREDIT
//
// Picking a higher difficulty finds fewer shares worth proportionally more, so
// the payout rate depends only on the real hash rate. The coordinator enforces
// a minimum difficulty so verification cost per credit stays bounded.

export const MICRO = 1_000_000n;

export function workForBits(bits: number): bigint {
  return 1n << BigInt(bits);
}

/** Micro-credits earned by one accepted share at `bits` difficulty. */
export function microCreditsForShare(bits: number, workPerCredit: bigint): bigint {
  if (workPerCredit <= 0n) throw new Error('workPerCredit must be positive');
  return (workForBits(bits) * MICRO) / workPerCredit;
}

export function microToCredits(micro: bigint | number): number {
  return Number(micro) / 1e6;
}

export function creditsToMicro(credits: number): bigint {
  if (!Number.isFinite(credits)) throw new Error('credits must be finite');
  return BigInt(Math.round(credits * 1e6));
}

export function formatCredits(credits: number): string {
  if (!Number.isFinite(credits)) return '0';
  const abs = Math.abs(credits);
  return credits.toFixed(abs >= 100 ? 1 : abs >= 1 ? 2 : 4);
}

/** Expected credits per minute at a given hash rate (used for honest estimates only). */
export function creditsPerMinute(hashesPerSecond: number, workPerCredit: bigint): number {
  return (hashesPerSecond * 60) / Number(workPerCredit);
}
